// Fase 15A — Moduli & Feature: gerarchia GLOBAL → ORGANIZATION → COMPANY
// (punto 59) con cascade disable (punto 60). Solo moduli reali (punto 63).
import { useCallback, useEffect, useState } from "react";
import { Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function PlatformFeatures() {
  const [data, setData] = useState(null);
  const [level, setLevel] = useState("GLOBAL");
  const [orgId, setOrgId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (level === "ORGANIZATION" && orgId) params.organization_id = orgId;
      if (level === "COMPANY" && companyId) params.company_id = companyId;
      const [f, o, c] = await Promise.all([
        api.get("/admin/platform/features", { params }),
        api.get("/admin/platform/organizations"),
        api.get("/admin/platform/companies", { params: { limit: 200 } }),
      ]);
      setData(f.data);
      setOrgs(o.data.items || []);
      setCompanies(c.data.items || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api e setState sono stabili
  }, [level, orgId, companyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (module, enabled) => {
    try {
      await api.put("/admin/platform/features", {
        level, module, enabled,
        organization_id: level === "ORGANIZATION" ? orgId : (orgId || null),
        company_id: level === "COMPANY" ? companyId : null });
      toast.success(`${module} (${level}) → ${enabled ? "ON" : "OFF"}`);
      await load();
    } catch (e) {
      toast.error("Bloccato", { description: formatApiError(e) });
    }
  };

  const configFor = (module) => (data?.configs || []).find((c) =>
    c.module === module &&
    (level === "GLOBAL" ? c.level === "GLOBAL"
      : level === "ORGANIZATION" ? c.level === "ORGANIZATION" && c.organization_id === orgId
      : c.level === "COMPANY" && c.company_id === companyId));

  if (error) return <p className="text-sm text-red-700" data-testid="features-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="features-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-features-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Zap className="w-7 h-7 text-green-800" aria-hidden="true" /> Moduli & Feature
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Gerarchia GLOBAL → Studio → Azienda. Se un modulo è disattivato a livello
          superiore, i livelli inferiori non possono riattivarlo. Modulo attivo ≠
          permesso utente: servono entrambi.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label>Livello</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-44" data-testid="features-level-select">
              <SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">GLOBAL</SelectItem>
              <SelectItem value="ORGANIZATION">Studio</SelectItem>
              <SelectItem value="COMPANY">Azienda</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {level === "ORGANIZATION" && (
          <div className="space-y-1">
            <Label>Studio</Label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger className="w-64" data-testid="features-org-select">
                <SelectValue placeholder="Seleziona studio" /></SelectTrigger>
              <SelectContent>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {level === "COMPANY" && (
          <div className="space-y-1">
            <Label>Azienda</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-64" data-testid="features-company-select">
                <SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Card className="border-slate-200" data-testid="features-grid">
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Moduli — livello {level}
        </CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {data.modules.map((m) => {
            const cfg = configFor(m);
            const on = level === "COMPANY" && data.effective
              ? data.effective[m] : (cfg ? cfg.enabled : true);
            return (
              <button key={m} type="button"
                disabled={(level === "ORGANIZATION" && !orgId) || (level === "COMPANY" && !companyId)}
                onClick={() => toggle(m, !on)}
                data-testid={`feature-toggle-${m}`}
                className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors disabled:opacity-40 ${
                  on ? "bg-green-50 text-green-800 border-green-300 hover:bg-green-100"
                     : "bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200"}`}>
                {m} · {on ? "ON" : "OFF"}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {level === "COMPANY" && companyId && data.effective && (
        <p className="text-[11px] text-slate-500" data-testid="features-effective-note">
          Lo stato mostrato è quello EFFETTIVO per l'azienda selezionata, calcolato
          lungo tutta la gerarchia.
        </p>
      )}
      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300">
        Solo moduli realmente disponibili in piattaforma
      </Badge>
    </div>
  );
}
